/**
 * @file YouTubeVideo URL Constructor
 * @description This file defines a YouTubeVideo class that constructs YouTube embed URLs with various player parameters.
 *
 * @example
 * // Example usage:
 * import { YouTubeVideo } from './youtube_embed_url_constructor';
 *
 * const video = new YouTubeVideo('https://www.youtube.com/embed/your_video_id')
 *   .showSameChannelRelatedVideos()
 *   .autoplay()
 *   .mute()
 *   .captions();
 *
 */

import { getLang } from '@@/bits/current_locale'

class YouTubeVideo {
  private readonly baseEmbedUrl: string
  private readonly params: URLSearchParams

  constructor(embedUrl) {
    this.baseEmbedUrl = embedUrl
    this.params = new URLSearchParams()
  }

  // See https://developers.google.com/youtube/player_parameters for params
  showSameChannelRelatedVideos(): YouTubeVideo {
    this.params.set('rel', '0')
    return this
  }

  autoplay(): YouTubeVideo {
    this.params.set('autoplay', '1')
    return this
  }

  mute(): YouTubeVideo {
    this.params.set('mute', '1')
    return this
  }

  captions(): YouTubeVideo {
    this.params.set('cc_load_policy', '1')
    return this
  }

  enablejsapi(): YouTubeVideo {
    this.params.set('enablejsapi', '1')
    return this
  }

  setCurrentLang(): YouTubeVideo {
    this.params.set('cc_lang_pref', getLang())
    return this
  }

  get embedUrl(): string {
    return `${this.baseEmbedUrl}?${this.params.toString()}`
  }
}
export { YouTubeVideo }
